import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthService, LoggingService} from '@ld.consumer-portal/utils';
import { OriginationAuthService } from './origination-auth.service';

@Injectable({
  providedIn: 'root',
})
export class OriginationTokenRefreshService {
  private readonly authService = inject(AuthService);
  private readonly originationAuthService = inject(OriginationAuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loggingService = inject(LoggingService);

  private readonly CHECK_INTERVAL_MS = 60 * 1000;

  private readonly REFRESH_BUFFER_SECONDS = 300;

  private readonly _isMonitoring = signal(false);
  readonly isMonitoring = this._isMonitoring.asReadonly();

  private readonly _isRefreshing = signal(false);
  readonly isRefreshing = this._isRefreshing.asReadonly();

  private checkSubscription: Subscription | null = null;

  startMonitoring(): void {
    if (this._isMonitoring()) {
      return;
    }

    this._isMonitoring.set(true);

    this.checkSubscription = interval(this.CHECK_INTERVAL_MS)
      .pipe(
        filter(() => this.authService.hasCpContext()),
        filter(() => !this._isRefreshing()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkAndRefresh();
      });

    this.checkAndRefresh();
  }

  stopMonitoring(): void {
    this._isMonitoring.set(false);
    this.checkSubscription?.unsubscribe();
    this.checkSubscription = null;
  }

  private checkAndRefresh(): void {
    if (!this.authService.hasCpContext()) {
      return;
    }

    if (this.authService.isCpTokenExpiringSoon(this.REFRESH_BUFFER_SECONDS)) {
      this.triggerRefresh();
    }
  }

  private async triggerRefresh(): Promise<void> {
    if (this._isRefreshing()) {
      return;
    }

    this._isRefreshing.set(true);

    try {
      const result = await this.originationAuthService.refreshCpToken();

      if (!result) {
        this.loggingService.logWarning('CP token refresh failed - redirecting to login');
        await this.handleRefreshFailure();
      }
    } catch (error) {
      this.loggingService.logException(error, 'CP token refresh error:');
      await this.handleRefreshFailure();
    } finally {
      this._isRefreshing.set(false);
    }
  }

  private async handleRefreshFailure(): Promise<void> {
    this.stopMonitoring();
    this.originationAuthService.logout();
    await this.router.navigate(['/login'], {
      queryParams: { reason: 'session_expired' },
      replaceUrl: true,
    });
  }

  async forceRefresh(): Promise<boolean> {
    if (this._isRefreshing()) {
      await this.waitForRefresh();
      return this.authService.hasCpContext();
    }

    this._isRefreshing.set(true);

    try {
      const result = await this.originationAuthService.refreshCpToken();
      return !!result;
    } catch (error) {
      this.loggingService.logException(error, 'Forced CP token refresh failed:');
      return false;
    } finally {
      this._isRefreshing.set(false);
    }
  }

  private waitForRefresh(): Promise<void> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!this._isRefreshing()) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 10000);
    });
  }
}
